// Shared worlds: links one Explore session to its room. Friends, emotes, Team Lessons and stars live here;
// block edits go through WorldSync so solo, offline and shared play all use the same world.
import * as THREE from 'three';
import { RoomClient } from './room-client.js';
import { WorldSync } from './world-sync.js';
import { PeerManager } from './peers.js';
import { StarBeacon } from './monument.js';
import { identity } from './identity.js';
import { friendsChipHTML, friendsPanelHTML, emoteRowHTML, worldsOnlineHTML, EMOTES, EMOTE_ICONS } from './friends-ui.js';
import { LobbyPoller } from './lobby.js';
import { giveOrKeep } from './expansion-rules.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { escapeHtml } from './util.js';

const MILESTONE_GEMS = 1;
const GUEST_GRACE = 15; // seconds a visitor may be disconnected before heading home

export function helloFrame(save) {
  return {
    t: 'hello',
    v: 1,
    token: identity.data?.token,
    avatar: { tone: save.avatar.tone, hair: save.avatar.hair, style: save.avatar.style },
    equip: { skin: save.equip.skin, sword: save.equip.sword },
    pos: null,
  };
}

export class Multiplayer {
  constructor(game, explore, { mode = 'own', code, client = null, snapshot = null } = {}) {
    this.game = game;
    this.explore = explore;
    this.mode = mode;
    this.code = code;
    this.client = client || new RoomClient();
    this.status = this.client.status || 'connecting';
    this.room = null;
    this.you = null;
    this.members = [];
    this.lessons = new Map();
    this.cheer = null;
    this.emoteOpen = false;
    this.selfEmote = null;
    this.offlineFor = 0;
    this.time = 0;
    this.peers = new PeerManager(game, explore);
    this.beacon = new StarBeacon(explore.group);
    const d = game.save.data.discovery;
    this.sync = new WorldSync({
      client: this.client,
      host: explore,
      mode,
      me: explore.me,
      worldOwner: explore.worldOwner,
      pending: mode === 'own' ? d.pending : [],
      discovery: mode === 'own' ? d : null,
    });
    const c = this.client;
    this.off = [
      c.on('status', (s) => this.onStatus(s)),
      c.on('closed', (e) => this.onClosed(e)),
      c.on('snapshot', (m) => this.onSnapshot(m)),
      c.on('join', (m) => this.onJoin(m.peer)),
      c.on('leave', (m) => this.onLeave(m.id)),
      c.on('p', (m) => this.peers.pos(m.id, m)),
      c.on('emote', (m) => this.onEmote(m.id, m.k)),
      c.on('lesson', (m) => this.onLesson(m)),
      c.on('stars', (m) => this.onStars(m)),
      c.on('room', (m) => this.onRoom(m)),
      c.on('members', (m) => {
        this.members = m.members || [];
        this.refreshPanel();
      }),
      c.on('reset', () => this.onReset()),
    ];
    if (snapshot) {
      this.sync.handleSnapshot?.(snapshot);
      this.onSnapshot(snapshot);
    }
    if (mode === 'own') this.client.open(code, helloFrame(game.save.data), { retry: true }).catch(() => {});
  }

  get count() {
    return this.peers.count;
  }

  get isOwner() {
    return this.mode === 'own';
  }

  get ownerName() {
    return this.room?.ownerName || (this.isOwner ? this.game.save.data.name : 'your friend');
  }

  record(op) {
    this.sync.record(op);
  }

  // ---------- room events ----------
  onStatus(s) {
    this.status = s;
    if (s === 'online') this.offlineFor = 0;
    this.explore.refreshHUD();
  }

  onClosed({ code, fatal }) {
    if (!fatal) return;
    const owner = this.ownerName;
    const text = {
      4001: 'Please tell me who’s playing again, then try once more.',
      4002: `${owner}’s world is locked right now.`,
      4003: `${owner} asked you to leave. Your own world is always ready!`,
      4004: `${owner}’s world is full right now. Try again soon!`,
      4005: 'We couldn’t find that world. Check the code with your friend.',
      4006: 'You opened this world on another screen.',
    }[code];
    if (code === 4001) identity.setToken(null, 0);
    this.status = 'none';
    if (this.mode === 'guest') return this.leaveWorld(text || 'You left your friend’s world.');
    if (text) this.game.ui.toast(text, 'blue', 5000);
    this.peers.setAll([]);
    this.explore.refreshHUD();
  }

  onSnapshot(m) {
    this.room = m.room;
    this.you = m.you;
    this.members = m.members || [];
    if (m.you?.token) identity.setToken(m.you.token, m.you.exp);
    // Settings → "Show my world in Friends playing now" reaches your own world here.
    if (this.isOwner && typeof m.room?.listed === 'boolean' && m.room.listed !== this.wantListed) this.setListed(this.wantListed);
    this.peers.setAll(m.peers || []);
    this.beacon.set(m.room?.stars || 0);
    if (this.isOwner) this.catchUpMilestones(m.room?.stars || 0);
    this.sendPos(true);
    this.explore.refreshHUD();
    this.refreshPanel();
  }

  onJoin(peer) {
    // A friend reconnecting sends `join` again: only a new arrival gets the hello.
    if (this.peers.add(peer)) {
      this.game.ui.toast(`👋 ${escapeHtml(peer.name)} joined!`, 'green', 3000);
      audio.play('pop');
      haptics.play('pop');
    }
    this.sendPos(true);
    this.explore.refreshHUD();
    this.refreshPanel();
  }

  onLeave(id) {
    const name = this.peers.list().find((p) => p.id === id)?.name;
    this.peers.remove(id);
    this.lessons.delete(id);
    if (name) this.game.ui.toast(`${escapeHtml(name)} headed home. See you soon!`, 'blue', 2500);
    this.explore.refreshHUD();
    this.refreshPanel();
  }

  onEmote(id, k) {
    this.peers.emote(id, k);
    audio.play('pop', { pitch: 1.2 });
  }

  onLesson(m) {
    if (m.ev === 'start') {
      this.lessons.set(m.id, { name: m.name, station: m.station, x: m.x, z: m.z, until: this.time + 90 });
      this.peers.say(m.id, '📚', 4);
    } else if (m.ev === 'done') {
      this.lessons.delete(m.id);
      this.cheer = { id: m.id, name: m.name, until: this.time + 8 };
    }
  }

  onStars(m) {
    if (this.room) this.room.stars = m.stars;
    this.beacon.set(m.stars);
    this.beacon.celebrate?.();
    const mine = m.by === this.you?.id;
    this.game.ui.toast(mine ? `⭐ Team star! This world has ${m.stars}.` : `⭐ ${escapeHtml(m.name)} earned a team star!`, 'green', 3200);
    if (!m.milestone) haptics.play('reward');
    if (m.milestone) this.celebrate(m.milestone);
    this.explore.refreshHUD();
  }

  onRoom(m) {
    if (this.room) {
      this.room.locked = !!m.locked;
      if (typeof m.listed === 'boolean') this.room.listed = m.listed;
    }
    this.refreshPanel();
  }

  onReset() {
    this.game.ui.toast(this.isOwner ? 'A fresh world! What will you build first? 🌱' : `${escapeHtml(this.ownerName)} started their world again.`, 'green', 4500);
  }

  // Your own world's milestones reward you once, even if they were reached while you were away.
  catchUpMilestones(stars) {
    const marks = this.game.save.data.discovery.teamMilestones;
    const code = this.room?.code || this.code;
    const reached = [10, 25, 50, 100, 250, 500, 1000].filter((n) => n <= stars && n > (marks[code] || 0));
    if (!reached.length) return;
    marks[code] = reached[reached.length - 1];
    this.game.save.data.gems += reached.length * MILESTONE_GEMS;
    this.game.save.persist();
    this.game.ui.toast(`🎆 Your world reached ${marks[code]} team stars! +${reached.length * MILESTONE_GEMS} 💎`, 'green', 5000);
  }

  celebrate(n) {
    const marks = this.game.save.data.discovery.teamMilestones;
    const code = this.room?.code || this.code;
    const p = this.explore.player.pos;
    for (let i = 0; i < 3; i++)
      this.game.fx.particles.confetti(new THREE.Vector3(p.x + (i - 1) * 2, p.y + 2.5, p.z), 90, 2.5);
    audio.play('achievement');
    haptics.play('levelComplete');
    if ((marks[code] || 0) < n) {
      marks[code] = n;
      this.game.save.data.gems += MILESTONE_GEMS;
      this.game.save.persist();
    }
    this.game.ui.toast(`🎆 ${n} team stars! You learned this together. +${MILESTONE_GEMS} 💎`, 'green', 5000);
  }

  // ---------- your own actions ----------
  emote(k) {
    if (!EMOTES.includes(k)) return;
    if (this.game.paused && this.panelOpen) {
      this.panelOpen = false;
      this.game.resume();
    }
    this.emoteOpen = false;
    const E = this.explore;
    if (k === 'wave') E.waveTime = E.time + 1.6;
    if (k === 'dance' || k === 'cheer') E.danceTime = E.time + 1.6;
    this.selfEmote = { html: `<span class="peer-emote">${EMOTE_ICONS[k]}</span>`, until: this.time + 2.5 };
    audio.play('pop');
    this.client.send({ t: 'emote', k });
    if (k === 'cheer') this.cheer = null;
    E.refreshHUD();
  }

  toggleEmotes() {
    this.emoteOpen = !this.emoteOpen;
    this.explore.refreshHUD();
  }

  lessonStart(station) {
    this.client.send({ t: 'lesson', ev: 'start', station: station.id, x: Math.round(station.x), z: Math.round(station.z) });
  }

  lessonDone(station, result) {
    if (!(result?.correct > 0)) return; // partial lessons still count
    this.client.send({ t: 'lesson', ev: 'done', station: station.id, correct: result.correct });
  }

  // A friend's lesson nearby, or a friend who just finished one.
  prompt(pos) {
    if (this.cheer && this.cheer.until > this.time)
      return `<button class="btn btn-yellow cheer-btn" data-act="emote" data-k="cheer">🎉 Cheer for ${escapeHtml(this.cheer.name)}</button>`;
    for (const [id, l] of this.lessons) {
      if (l.until < this.time) {
        this.lessons.delete(id);
        continue;
      }
      if (Math.hypot(pos.x - l.x - 0.5, pos.z - l.z - 0.5) < 10 && this.explore.stations.some((s) => s.id === l.station))
        return `<button class="btn btn-purple lesson-join" data-act="joinLesson" data-id="${escapeHtml(l.station)}">📚 Join ${escapeHtml(l.name)}’s lesson <small>Team ⭐</small></button>`;
    }
    return '';
  }

  joinLesson(station) {
    this.explore.learn(station, { joined: true });
  }

  sendPos(force = false) {
    const E = this.explore, p = E.player;
    // Nobody to see you: save the free-plan request budget.
    if (!p || this.status !== 'online' || !this.count) return;
    this.client.sendPos(
      {
        x: p.pos.x,
        y: p.pos.y,
        z: p.pos.z,
        yw: p.facing,
        vy: p.vel.y,
        g: p.onGround ? 1 : 0,
        sw: p.swingT,
        sp: Math.hypot(p.vel.x, p.vel.z),
        r: E.expansion?.mount || E.expansion?.rail ? 1 : 0,
      },
      force,
    );
  }

  update(dt) {
    this.time += dt;
    this.peers.update(dt);
    this.beacon.update(dt, this.time);
    const E = this.explore, ui = this.game.fx.worldUI;
    if (this.selfEmote && this.selfEmote.until > this.time && E.character) {
      ui.anchor('me:emote', this.selfEmote.html, E.character.root.position.clone().add(new THREE.Vector3(0, 2.7, 0)), 'bubble');
    } else if (this.selfEmote) {
      ui.hide('me:emote');
      this.selfEmote = null;
    }
    if (!this.game.paused && E.state === 'play') this.sendPos();
    if (this.mode === 'guest') {
      this.offlineFor = this.status === 'online' ? 0 : this.offlineFor + dt;
      if (this.offlineFor > GUEST_GRACE) this.leaveWorld(`We lost ${escapeHtml(this.ownerName)}’s world. Back to the menu…`);
    }
  }

  leaveWorld(text) {
    if (this.leaving) return;
    this.leaving = true;
    this.game.ui.toast(text, 'blue', 5000);
    setTimeout(() => this.game.showMenu(), 0);
  }

  // ---------- HUD and panel ----------
  chipHTML() {
    const html = friendsChipHTML({ status: this.status, count: this.count, stars: this.room?.stars || 0, guest: this.mode === 'guest' });
    return html + (this.emoteOpen && this.count ? emoteRowHTML() : '');
  }

  panelHTML() {
    const w = this.worlds;
    return friendsPanelHTML({
      role: this.isOwner ? 'owner' : 'guest',
      status: this.status,
      room: { code: this.room?.code || this.code, ownerName: this.ownerName, locked: !!this.room?.locked, stars: this.room?.stars || 0, hasBackup: !!this.room?.hasBackup },
      peers: this.peers.list(),
      members: this.members.filter((m) => m.id !== this.you?.id),
      myName: this.game.save.data.name,
      worlds: w?.ok ? w.worlds : null,
      worldsError: w && !w.ok ? w.error : '',
      myCode: identity.data?.roomCode || '',
    });
  }

  openPanel() {
    const E = this.explore;
    if (E.state !== 'play') return;
    E.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    this.panelOpen = true;
    this.game.ui.modal(this.panelHTML(), 'modal-journal modal-friends');
    // "Friends playing now" asks every 15 s while the panel is open (it stops itself once it closes).
    if (!this.lobby?.running) {
      this.lobby = new LobbyPoller({ every: 15000, active: () => this.panelShowing(), onUpdate: (r) => this.showWorlds(r) });
      this.lobby.start();
    }
  }

  panelShowing() {
    return !!(this.panelOpen && this.game.paused && this.game.ui.modalEl.classList.contains('modal-friends'));
  }

  refreshPanel() {
    if (this.panelShowing()) this.game.ui.modal(this.panelHTML(), 'modal-journal modal-friends');
    else this.panelOpen = false;
  }

  // Only the list is redrawn, and only when it changed, so the panel keeps its scroll position.
  showWorlds(r) {
    const html = worldsOnlineHTML({ worlds: r.ok ? r.worlds : null, myCode: identity.data?.roomCode || '', error: r.ok ? '' : r.error, here: this.room?.code || this.code });
    const changed = html !== this.worldsHTML;
    this.worlds = r;
    this.worldsHTML = html;
    const box = this.panelShowing() && this.game.ui.modalEl.querySelector('.worlds-online');
    if (box && changed) box.innerHTML = html;
  }

  // ---------- owner tools ----------
  lock() {
    if (!this.isOwner) return;
    this.client.send({ t: 'lock', on: !this.room?.locked });
  }

  get wantListed() {
    return this.game.save.data.settings.worldListed !== false;
  }

  // Show (or hide) your own world in "Friends playing now". Hidden worlds can still be visited by code.
  setListed(on) {
    if (!this.isOwner) return false;
    return this.client.send({ t: 'listed', on: on ? 1 : 0 });
  }

  async kick(id) {
    if (!this.isOwner) return;
    const name = this.peers.list().find((p) => p.id === id)?.name || 'your friend';
    const ok = await this.game.ui.confirm(`Ask ${escapeHtml(name)} to leave?`, `${escapeHtml(name)} goes back to their own world. You can let them back in from this panel.`, 'Ask to leave', 'Cancel');
    if (ok) this.client.send({ t: 'kick', id });
    this.openPanel();
  }

  unkick(id) {
    if (this.isOwner) this.client.send({ t: 'unkick', id });
  }

  async reset() {
    if (!this.isOwner) return;
    const ui = this.game.ui;
    const ok = await ui.confirm('Start your world again?', 'Every block goes, including friends’ builds. Chest items come back to your bag. Your coins, gems, tools, stamps and badges are safe.', 'Start again…', 'Keep my world');
    if (!ok) return this.openPanel();
    const sure = await ui.confirm('Really start again?', 'You can bring this world back later from the Friends panel.', 'Yes, start again', 'Keep my world');
    if (!sure) return this.openPanel();
    this.refundChests();
    this.client.send({ t: 'reset' });
    ui.closeModal();
    this.game.resume();
  }

  restore() {
    if (!this.isOwner) return;
    this.client.send({ t: 'restore' });
    this.game.ui.closeModal();
    this.game.resume();
  }

  refundChests() {
    const d = this.explore.data;
    for (const dev of Object.values(d.expansion.devices || {}))
      for (const [id, n] of Object.entries(dev.stash || {})) {
        if (n > 0) giveOrKeep(d, id, n); // a full stack keeps the rest in Lost & found
        delete dev.stash[id];
      }
    this.game.save.persist();
  }

  dispose() {
    this.lobby?.stop();
    for (const off of this.off) off?.();
    this.sync.dispose?.();
    this.client.close();
    this.peers.dispose();
    this.beacon.dispose();
    this.game.fx.worldUI.hide('me:emote');
  }
}
